// Chat Widget Constants
// Questions, languages, and localized messages

export const QUESTIONS = [
  {
    id: 'greeting',
    type: 'message',
    text: "Hi! I'm here to help you get started with your case. Were you recently involved in an accident?",
    field: 'accident_involved',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false }
    ]
  },
  {
    id: 'accident_type',
    type: 'select',
    text: "What type of accident was it?",
    field: 'accident_type',
    options: [
      { label: 'Car Accident', value: 'car' },
      { label: 'Truck Accident', value: 'truck' },
      { label: 'Motorcycle Accident', value: 'motorcycle' },
      { label: 'Slip & Fall', value: 'slip_fall' },
      { label: 'Workplace Injury', value: 'workplace' },
      { label: 'Pedestrian Accident', value: 'pedestrian' },
      { label: 'Other', value: 'other' }
    ]
  },
  {
    id: 'accident_date',
    type: 'date',
    text: "When did the accident happen?",
    field: 'accident_date'
  },
  {
    id: 'injured',
    type: 'yesno',
    text: "Were you injured in the accident?",
    field: 'injured'
  },
  {
    id: 'injury_description',
    type: 'text',
    text: "Please describe your injuries:",
    field: 'injury_description',
    conditional: { field: 'injured', value: true }
  },
  {
    id: 'medical_treatment',
    type: 'yesno',
    text: "Have you received any medical treatment?",
    field: 'medical_treatment'
  },
  {
    id: 'treatment_details',
    type: 'text',
    text: "What type of treatment have you received?",
    field: 'treatment_details',
    conditional: { field: 'medical_treatment', value: true }
  },
  {
    id: 'has_lawyer',
    type: 'yesno',
    text: "Do you currently have a lawyer representing you?",
    field: 'has_lawyer'
  },
  {
    id: 'file_upload',
    type: 'file',
    text: "Would you like to upload any documents? (Photos, police reports, medical records, etc.)",
    field: 'file_urls',
    optional: true
  },
  {
    id: 'client_name',
    type: 'text',
    text: "Great! Now let me get your contact information. What's your full name?",
    field: 'client_name'
  },
  {
    id: 'client_email',
    type: 'email',
    text: "What's your email address?",
    field: 'client_email'
  },
  {
    id: 'client_phone',
    type: 'phone',
    text: "And your phone number?",
    field: 'client_phone'
  }
];

export const AVAILABLE_LANGUAGES = [
  // Primary languages
  { code: 'en', name: 'English', native: 'English' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'tl', name: 'Tagalog', native: 'Tagalog' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  // Americas
  { code: 'ht', name: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
  { code: 'nv', name: 'Navajo', native: 'Diné Bizaad' },
  // Europe
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'pl', name: 'Polish', native: 'Polski' },
  { code: 'uk', name: 'Ukrainian', native: 'Українська' },
  { code: 'el', name: 'Greek', native: 'Ελληνικά' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands' },
  // Asia
  { code: 'th', name: 'Thai', native: 'ไทย' },
  { code: 'id', name: 'Indonesian', native: 'Bahasa Indonesia' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'ur', name: 'Urdu', native: 'اردو' },
  { code: 'fa', name: 'Persian', native: 'فارسی' },
  // Middle East/Africa
  { code: 'he', name: 'Hebrew', native: 'עברית' },
  { code: 'tr', name: 'Turkish', native: 'Türkçe' },
  { code: 'am', name: 'Amharic', native: 'አማርኛ' },
  { code: 'sw', name: 'Swahili', native: 'Kiswahili' },
];

export const LOCALIZED_MESSAGES = {
  processing: {
    en: "Thank you! I'm processing your information...",
    es: "¡Gracias! Estoy procesando su información...",
    zh: "谢谢！我正在处理您的信息...",
    vi: "Cảm ơn! Tôi đang xử lý thông tin của bạn...",
    ko: "감사합니다! 정보를 처리하고 있습니다...",
    fr: "Merci ! Je traite vos informations...",
    pt: "Obrigado! Estou processando suas informações...",
    ar: "شكراً! أقوم بمعالجة معلوماتك...",
    ru: "Спасибо! Обрабатываю вашу информацию...",
    hi: "धन्यवाद! मैं आपकी जानकारी प्रोसेस कर रहा हूं...",
    ja: "ありがとうございます！情報を処理しています...",
    de: "Danke! Ich verarbeite Ihre Informationen...",
    it: "Grazie! Sto elaborando le tue informazioni...",
    tl: "Salamat! Pinoproseso ko ang iyong impormasyon..."
  },
  status: {
    en: {
      viable: "Great news! Based on your information, it looks like an attorney may be able to help you.",
      review: "Thank you for your submission. An attorney will review your case and contact you soon.",
      unlikely: "Thank you for reaching out. An attorney will review your information."
    },
    es: {
      viable: "¡Buenas noticias! Según su información, parece que un abogado puede ayudarle.",
      review: "Gracias por su envío. Un abogado revisará su caso y se comunicará con usted pronto.",
      unlikely: "Gracias por comunicarse. Un abogado revisará su información."
    },
    zh: {
      viable: "好消息！根据您提供的信息，律师可能能够帮助您。",
      review: "感谢您的提交。律师将审查您的案件并尽快与您联系。",
      unlikely: "感谢您的联系。律师将审查您的信息。"
    },
    vi: {
      viable: "Tin tốt! Dựa trên thông tin của bạn, có vẻ như luật sư có thể giúp bạn.",
      review: "Cảm ơn bạn đã gửi thông tin. Luật sư sẽ xem xét trường hợp của bạn và liên hệ sớm.",
      unlikely: "Cảm ơn bạn đã liên hệ. Luật sư sẽ xem xét thông tin của bạn."
    },
    fr: {
      viable: "Bonne nouvelle ! D'après vos informations, il semble qu'un avocat puisse vous aider.",
      review: "Merci pour votre soumission. Un avocat examinera votre cas et vous contactera bientôt.",
      unlikely: "Merci de nous avoir contactés. Un avocat examinera vos informations."
    },
    ko: {
      viable: "좋은 소식입니다! 귀하의 정보에 따르면 변호사가 도움을 드릴 수 있을 것 같습니다.",
      review: "제출해 주셔서 감사합니다. 변호사가 귀하의 사건을 검토하고 곧 연락드리겠습니다.",
      unlikely: "연락해 주셔서 감사합니다. 변호사가 귀하의 정보를 검토할 것입니다."
    }
  },
  schedule: {
    en: "Would you like to schedule a free consultation?",
    es: "¿Le gustaría programar una consulta gratuita?",
    zh: "您想预约免费咨询吗？",
    vi: "Bạn có muốn đặt lịch tư vấn miễn phí không?",
    fr: "Souhaitez-vous planifier une consultation gratuite ?",
    ko: "무료 상담을 예약하시겠습니까?",
    pt: "Gostaria de agendar uma consulta gratuita?",
    ar: "هل ترغب في تحديد موعد لاستشارة مجانية؟",
    ru: "Хотите записаться на бесплатную консультацию?",
    de: "Möchten Sie eine kostenlose Beratung vereinbaren?",
    ja: "無料相談のご予約はいかがですか？"
  },
  error: {
    en: "I'm sorry, there was an error submitting your information. Please try again.",
    es: "Lo siento, hubo un error al enviar su información. Por favor, inténtelo de nuevo.",
    zh: "抱歉，提交您的信息时出错。请重试。",
    vi: "Xin lỗi, đã xảy ra lỗi khi gửi thông tin của bạn. Vui lòng thử lại.",
    fr: "Désolé, une erreur s'est produite lors de l'envoi de vos informations. Veuillez réessayer.",
    ko: "죄송합니다. 정보 제출 중 오류가 발생했습니다. 다시 시도해 주세요."
  }
};

// Storage keys
export const LANGUAGE_STORAGE_KEY = 'intakepal_language_preference';
export const COOKIE_CONSENT_KEY = 'intakepal_cookie_consent';
